import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import JobsService from 'services/jobs';

export const JOB_STATUSES = {
  IDLE: 'idle',
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

// Backend returns numeric status codes (Django Job model)
const BACKEND_STATUS_MAP = {
  0: JOB_STATUSES.PENDING,
  1: JOB_STATUSES.PROCESSING,
  2: JOB_STATUSES.COMPLETED,
  3: JOB_STATUSES.FAILED,
};

/**
 * Generic hook for polling job status.
 *
 * @param {object} options
 * @param {string|null} options.jobId - Job ID to poll. Pass null to not start polling.
 * @param {number} options.interval - Polling interval in ms. Default 3000.
 * @param {number} options.maxFailures - Max consecutive fetch failures before stopping. Default 3.
 * @param {function} options.onCompleted - Called with output_payload when job completes.
 * @param {function} options.onFailed - Called with error when job fails.
 */
export const useJobPolling = ({
  jobId = null,
  interval = 3000,
  maxFailures = 3,
  onCompleted,
  onFailed,
} = {}) => {
  const [jobPollStatus, setJobPollStatus] = useState(JOB_STATUSES.IDLE);
  const [jobData, setJobData] = useState(null);
  const [error, setError] = useState(null);

  const intervalRef = useRef(null);
  const failureCountRef = useRef(0);
  const isCancelledRef = useRef(false);

  // Latest-ref pattern: avoid re-creating fetchStatus when callbacks change
  const onCompletedRef = useRef(onCompleted);
  const onFailedRef = useRef(onFailed);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);
  useEffect(() => {
    onFailedRef.current = onFailed;
  }, [onFailed]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async activeJobId => {
    if (isCancelledRef.current) {
      return;
    }

    try {
      const data = await JobsService.getJobStatus(activeJobId);

      if (isCancelledRef.current) {
        return;
      }

      failureCountRef.current = 0;
      setJobData(data);

      const mappedStatus = BACKEND_STATUS_MAP[data.status] || data.status;

      if (mappedStatus === JOB_STATUSES.COMPLETED) {
        setJobPollStatus(JOB_STATUSES.COMPLETED);
        stopPolling();
        onCompletedRef.current?.(data.output_payload);
      }
      else if (mappedStatus === JOB_STATUSES.FAILED) {
        setJobPollStatus(JOB_STATUSES.FAILED);
        setError(data.error);
        stopPolling();
        onFailedRef.current?.(data.error);
      }
      else {
        setJobPollStatus(mappedStatus);
      }
    }
    catch (err) {
      if (isCancelledRef.current) {
        return;
      }

      failureCountRef.current += 1;
      Sentry.captureException(err);

      if (failureCountRef.current >= maxFailures) {
        setJobPollStatus(JOB_STATUSES.FAILED);
        setError('Polling failed after multiple attempts');
        stopPolling();
        onFailedRef.current?.('Polling failed after multiple attempts');
      }
    }
  }, [stopPolling, maxFailures]);

  // Start/stop polling when jobId changes
  useEffect(() => {
    if (!jobId) {
      stopPolling();

      return;
    }

    isCancelledRef.current = false;
    failureCountRef.current = 0;
    setJobPollStatus(JOB_STATUSES.PENDING);
    setError(null);

    // Poll immediately, then on interval
    fetchStatus(jobId);
    intervalRef.current = setInterval(() => fetchStatus(jobId), interval);

    return () => {
      isCancelledRef.current = true;
      stopPolling();
    };
  }, [jobId, interval, fetchStatus, stopPolling]);

  return {
    jobPollStatus,
    jobData,
    error,
    stopPolling,
  };
};
